import { useCallback, useState } from 'react';
import { PlayCircle, VideoLibrary } from '@mui/icons-material';
import { Tooltip, Box } from '@mui/material';
import { VideoTriggerButton, VideoContainer, PlaceholderImage } from './VideoDisplay.styled';
import { Popup } from '../Popup/Popup';

interface VideoDisplayProps {
  videoUrl: string;
  thumbnailUrl?: string;
  tooltipTitle?: string;
  width?: number | string;
  height?: number | string;
  alt?: string;
}

export function VideoDisplay({
  videoUrl,
  thumbnailUrl,
  tooltipTitle = 'Click to play video',
  width = 800,
  height = 600,
  alt = 'Video thumbnail',
}: VideoDisplayProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleVideoClick = useCallback(() => {
    if (!videoUrl) {
      console.error('VideoDisplay: Missing video URL');
      return;
    }
    setIsOpen(true);
  }, [videoUrl]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <>
      <VideoContainer 
        onClick={handleVideoClick}
        style={{ 
          width: width || '100%',
          height: height || '100%',
          minHeight: typeof height === 'number' ? Math.min(height, 200) : 200
        }}
        data-testid="video-container"
      >
        {thumbnailUrl ? (
          <PlaceholderImage 
            src={thumbnailUrl} 
            alt={alt}
            data-testid="video-thumbnail"
          />
        ) : (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
            data-testid="video-placeholder"
          >
            <VideoLibrary 
              sx={{ 
                fontSize: '64px',
                color: 'rgba(0, 0, 0, 0.3)',
              }}
              data-testid="video-placeholder-icon"
            />
          </Box>
        )}
        <Tooltip title={tooltipTitle}>
          <VideoTriggerButton
            onClick={(e) => {
              e.stopPropagation();
              handleVideoClick();
            }}
            aria-label="Play video"
            data-testid="video-play-button"
          >
            <PlayCircle data-testid="video-play-icon" />
          </VideoTriggerButton>
        </Tooltip>
      </VideoContainer>

      <Popup
        open={isOpen}
        onClose={handleClose}
        width={typeof width === 'number' ? width : 800}
        height={typeof height === 'number' ? height : 600}
        data-testid="video-popup"
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          data-testid="video-popup-content"
        >
          <video
            controls
            autoPlay
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
            data-testid="video-player"
          >
            <source src={videoUrl} data-testid="video-source" />
            Your browser does not support the video tag.
          </video>
        </div>
      </Popup>
    </>
  );
} 